import { PlusOutlined } from "@ant-design/icons"
import { Button, Empty, Flex, Typography } from "antd"
import type { ColumnsType } from "antd/es/table"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { EntityTable } from "../../../components/EntityTable/EntityTable.tsx"
import { DEFAULT_TAB, TABLE_SURFACES } from "../../../components/EntityTable/surfaces.ts"
import { RowActionsMenu } from "../../../components/EntityTable/RowActionsMenu.tsx"
import { TableResultCount } from "../../../components/Table/TableResultCount.tsx"
import { generateDocumentTitle } from "../../../constants/document-title.ts"
import { RoutesPaths } from "../../../constants/routes.ts"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { useScrolledTable } from "../../../hooks/use-scrolled-table.ts"
import { useTableContentHeight } from "../../../hooks/use-table-content-height.ts"
import { selectActiveBranchId } from "../../Branches/redux/branches.slice.ts"
import { useCanCountersign } from "../hooks/use-countersign.ts"
import { useEnvelopeLifecycle } from "../hooks/use-envelope-lifecycle.tsx"
import { activeFilterCount, queryFromFilters } from "../helpers/tracking-query.ts"
import { useTrackingFilters } from "../hooks/use-tracking-filters.ts"
import { useTrackingList } from "../hooks/use-tracking-list.ts"
import { useTrackingFilterOptions, useTrackingStats, useTrackingSummary } from "../hooks/use-tracking-overview.ts"
import { EMPTY_FILTERS, type TrackedEnvelope, type TrackingFilters } from "../types/tracking.types.ts"
import { EnvelopeActivityDrawer } from "./components/EnvelopeActivityDrawer.tsx"
import { TrackingFiltersBar } from "./components/TrackingFiltersBar.tsx"
import { TrackingKpiRow } from "./components/TrackingKpiRow.tsx"
import { TrackingSummaryStrip } from "./components/TrackingSummaryStrip.tsx"
import { useTrackingColumns } from "./components/tracking-columns.tsx"

export default function SignatureRequestsPage() {
  const { t } = useTranslation()
  const { filters, setFilters, replaceFilters, clearFilters } = useTrackingFilters()
  const options = useTrackingFilterOptions()
  const query = useMemo(() => queryFromFilters(filters), [filters])
  const activeBranchId = useAppSelector(selectActiveBranchId) ?? undefined
  // Only the table's own list endpoint is branch-scoped backend-side —
  // the summary/stats endpoints are a separate aggregate this fix hasn't
  // touched, so `team` is added to the table's query alone.
  const listQuery = useMemo(
    () => ({ ...query, ...(activeBranchId ? { team: activeBranchId } : {}) }),
    [query, activeBranchId]
  )
  const stats = useTrackingStats()
  const summary = useTrackingSummary(query)
  const list = useTrackingList(listQuery)
  const navigate = useNavigate()
  const canCountersign = useCanCountersign()
  const [activityFor, setActivityFor] = useState<TrackedEnvelope | null>(null)
  const lifecycle = useEnvelopeLifecycle(list.reload)

  const actions = useMemo<ColumnsType<TrackedEnvelope>[number]>(
    () => ({
      title: t("esign.tracking.columns.actions"),
      key: "actions",
      width: 90,
      fixed: "right",
      className: "text-center",
      render: (_, row) => {
        const items = [
          { key: "activity", label: t("esign.tracking.actions.activity") },
          ...(canCountersign && row.stage === "awaiting_countersignature"
            ? [{ key: "countersign", label: t("esign.tracking.actions.countersign") }]
            : []),
          ...(lifecycle.mayResend(row.status)
            ? [{ key: "resend", label: t("esign.lifecycle.resend") }]
            : []),
          ...(lifecycle.mayVoid(row.status)
            ? [{ key: "void", label: t("esign.lifecycle.void"), danger: true }]
            : [])
        ]
        return (
          <Flex justify={"center"}>
            <RowActionsMenu
              items={items}
              buttonProps={{ "data-testid": `tracking-row-actions-${row.id}` } as never}
              onClick={({ key }) => {
                if (key === "activity") setActivityFor(row)
                if (key === "countersign") navigate(RoutesPaths.esign.sign(row.id))
                if (key === "resend") void lifecycle.resend(row.id)
                if (key === "void") lifecycle.startVoid(row.id, row.subject.name)
              }}
            />
          </Flex>
        )
      }
    }),
    [t, canCountersign, navigate, lifecycle]
  )
  const columns = useTrackingColumns({ actions, ordering: filters.ordering })
  const { tableRef, tableHeight, onTableScroll } = useScrolledTable({
    loading: list.loading,
    next: list.next,
    loadMore: list.loadMore,
    fullPage: true
  })
  const contentHeight = useTableContentHeight(tableRef)
  const height = contentHeight ? Math.min(tableHeight, contentHeight) : tableHeight

  const emptyText = list.failed ? (
    <Empty description={t("esign.tracking.empty.failed")}>
      <Button size={"small"} onClick={list.reload}>
        {t("esign.tracking.empty.retry")}
      </Button>
    </Empty>
  ) : (
    <Empty description={t(activeFilterCount(filters) ? "esign.tracking.empty.filtered" : "esign.tracking.empty.none")} />
  )

  useEffect(() => {
    generateDocumentTitle(t("esign.tracking.title"))
  }, [t])

  return (
    <Flex vertical gap={"middle"} className={"p-4"}>
      <Flex justify={"space-between"} align={"center"} wrap gap={"small"}>
        <Typography.Title level={3} className={"!mb-0"}>
          {t("esign.tracking.title")}
        </Typography.Title>
        {lifecycle.mayRaise && (
          <Button
            type={"primary"}
            icon={<PlusOutlined />}
            onClick={() => navigate(RoutesPaths.esign.newEnvelope({ scope: "company" }))}
          >
            {t("esign.tracking.newRequest")}
          </Button>
        )}
      </Flex>
      <TrackingKpiRow
        stats={stats}
        selected={filters.tile}
        onSelect={(tile) => replaceFilters({ ...EMPTY_FILTERS, tile, ordering: filters.ordering })}
      />
      <TrackingFiltersBar
        filters={filters}
        setFilters={setFilters}
        clearFilters={clearFilters}
        senders={options.data?.senders ?? []}
      />
      <TrackingSummaryStrip summary={summary} />
      <div ref={tableRef} className={"overflow-hidden rounded-lg border border-gray-200 bg-white"}>
        <EntityTable<TrackedEnvelope>
          surface={TABLE_SURFACES.esignTracking}
          tab={DEFAULT_TAB}
          columns={columns}
          dataSource={list.items}
          loading={list.loading && !list.items.length}
          rowKey={"id"}
          fitScrollX
          hideConfigButton
          withOutPagination
          pagination={false}
          locale={{ emptyText }}
          onScroll={onTableScroll}
          scroll={{ y: height }}
          onChange={(_pagination, _filters, sorter) => {
            const value = Array.isArray(sorter) ? sorter[0] : sorter
            const ordering = value?.order ? `${value.order === "descend" ? "-" : ""}${String(value.field)}` : null
            setFilters({ ordering: ordering as TrackingFilters["ordering"] })
          }}
        />
        <div className={"border-t border-gray-200"}>
          <TableResultCount
            loaded={list.items.length}
            total={list.count}
            loading={list.loading}
            next={list.next}
            retryNext={list.retryNext}
            onRetry={list.loadMore}
          />
        </div>
      </div>
      {activityFor && <EnvelopeActivityDrawer envelope={activityFor} onClose={() => setActivityFor(null)} />}
      {lifecycle.dialog}
    </Flex>
  )
}
